import type { OnboardingProgress } from '@tailoring-crm/shared';

/**
 * Where the spotlight tour stands, worked out from facts rather than remembered.
 *
 * The tour never stores "you are on step 4". The server already knows whether the shop has a
 * customer, a measurement and an order (`progress.tour`), and the address bar knows which screen
 * is open, so the step is a function of those two plus whatever dialog is up. A reload, a second
 * device or a detour through the Orders list all land on the right step for the same reason:
 * there is nothing to drift.
 *
 * Kept free of React and the DOM so every combination can be tested as a table.
 */

type TourFlags = OnboardingProgress['tour'];

/** The three real actions, then done. Each stage is "the first thing not yet true". */
export type TourStage = 'customer' | 'measurement' | 'order' | 'finished';

export type TourStepId =
  | 'nav-customers'
  | 'customer-add'
  | 'customer-form'
  /** On the customer list once a customer exists: "open the one you are working with". */
  | 'customer-open'
  | 'customer-tab-measurements'
  | 'measure-add'
  | 'measure-save'
  | 'order-entry'
  | 'order-form';

export interface TourContext {
  pathname: string;
  /** A modal `<dialog>` is open, which makes everything behind it inert. */
  dialogOpen: boolean;
  /** The customer page's measurements pane is showing (its garment picker is on screen). */
  measurementsTabOpen: boolean;
}

export type TourResolution =
  | { kind: 'step'; step: TourStepId }
  /** Mid-transition — a save whose tick has not come back yet. Show nothing rather than flash. */
  | { kind: 'waiting' }
  /** On a screen the current stage has no business on; the tour offers a way back instead. */
  | { kind: 'off-route' }
  | { kind: 'finished' };

export function tourStage(tour: TourFlags): TourStage {
  if (tour.orderBooked) return 'finished';
  if (!tour.customerAdded) return 'customer';
  if (!tour.measurementSaved) return 'measurement';
  return 'order';
}

/** The customer a `/customers/:id` path is about; null for the list, the new form and the rest. */
export function customerIdFrom(pathname: string): string | null {
  const match = /^\/customers\/([^/]+)\/?$/.exec(pathname);
  if (!match?.[1] || match[1] === 'new') return null;
  return match[1];
}

export function resolveTourStep(tour: TourFlags, ctx: TourContext): TourResolution {
  const stage = tourStage(tour);
  if (stage === 'finished') return { kind: 'finished' };

  const { pathname, dialogOpen } = ctx;

  // Booking is the destination, so whoever reaches the booking screen early — "Save & book
  // order" on the customer form skips the measurement — is coached through it, not turned back.
  if (pathname === '/orders/new') {
    return dialogOpen ? { kind: 'waiting' } : { kind: 'step', step: 'order-form' };
  }

  const customerId = customerIdFrom(pathname);

  // Past the first customer, the list is where Resume lands when this browser does not know
  // which customer the tour was on. Pointing at the list is all it can do — a row is the
  // shop's own choice — so it says so rather than offering Resume again in a loop.
  if (stage !== 'customer' && pathname === '/customers') {
    return dialogOpen ? { kind: 'waiting' } : { kind: 'step', step: 'customer-open' };
  }

  if (stage === 'customer') {
    if (pathname === '/customers/new') return { kind: 'step', step: 'customer-form' };
    // Just saved: the form has moved on to the customer's page and the tick is on its way.
    if (customerId) return { kind: 'waiting' };
    if (dialogOpen) return { kind: 'waiting' };
    if (pathname === '/customers') return { kind: 'step', step: 'customer-add' };
    // Anywhere else the way in is the navigation, which is on every screen.
    return { kind: 'step', step: 'nav-customers' };
  }

  if (stage === 'measurement') {
    if (!customerId) return { kind: 'off-route' };
    if (dialogOpen) return { kind: 'step', step: 'measure-save' };
    return ctx.measurementsTabOpen
      ? { kind: 'step', step: 'measure-add' }
      : { kind: 'step', step: 'customer-tab-measurements' };
  }

  // stage === 'order'
  if (!customerId) return { kind: 'off-route' };
  return dialogOpen ? { kind: 'waiting' } : { kind: 'step', step: 'order-entry' };
}

/**
 * Where "Resume tour" takes somebody, so the stage they are on has a screen to happen on. The
 * customer they were working with is remembered when there is one; without it the list is the
 * honest fallback.
 */
export function resumePathFor(stage: TourStage, customerId: string | null): string | null {
  if (stage === 'customer') return '/customers';
  if (stage === 'measurement') {
    return customerId ? `/customers/${customerId}?tab=measurements` : '/customers';
  }
  if (stage === 'order') {
    return customerId ? `/orders/new?customerId=${customerId}` : '/orders/new';
  }
  return null;
}
